<template>
    <section class="slice slice-agenda">
        <div class="slice-content">
            <PrismicRichText v-if="slice.primary.title" :field="slice.primary.title" class="slice-title" />
            <div
                class="slice-body slice-agenda__body"
                :class="{'slice-agenda--no-calendar': !hasCalendar, 'slice-agenda--no-location': !hasLocation}"
            >
                <div class="slice-agenda__program">
                    <h3 class="slice-agenda__title">{{ slice.primary.calendarTitle }}</h3>
                    <div class="slice-agenda__items">
                        <div v-for="(item, i) in slice.primary.items" :key="`agenda-item-${i}`" class="slice-agenda__item">
                            <div class="slice-agenda__item__time">{{ item.time }}</div>
                            <PrismicRichText :field="item.text" class="slice-agenda__item__text" />
                        </div>
                    </div>
                </div>
                <div v-if="hasCalendar || hasLocation" class="slice-agenda__infos">
                    <div v-if="hasLocation" class="slice-agenda__infos__location">
                        <Location
                            v-if="hasLocation"
                            :title="slice.primary.eventLocationTitle!"
                            :image-url="eventMapImageUrl"
                            :image-alt="slice.primary.eventLocationMap.alt"
                        >
                            <PrismicRichText :field="slice.primary.eventLocationDescription" />
                        </Location>
                    </div>
                    <div v-if="hasCalendar" class="slice-agenda__infos__calendar">
                        <h3 class="slice-agenda__infos__calendar__text">{{ slice.primary.calendarInviteText }}</h3>
                        <div class="slice-agenda__infos__calendar__cta">
                            <MaltImg src="/cms-front/prismic/calendar.jpeg?vh=817296" :width="48" alt="" aria-hidden="true" />
                            <VJoyButton variant="main" size="small" class="slice-agenda__infos__calendar__button" :href="calendarInviteFileUrl">
                                {{ slice.primary.calendarInviteButtonText }}
                            </VJoyButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/client';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {AgendaSlice} from '@prismicio-types';
    import {VJoyButton} from '@maltjoy/core-vue';
    import Location from '~/components/Location.vue';

    const props = defineProps(getSliceComponentProps<AgendaSlice>(['slice', 'index', 'slices', 'context']));

    const calendarInviteFileUrl = computed(() =>
        isFilled.linkToMedia(props.slice.primary.calendarInviteFile) ? props.slice.primary.calendarInviteFile.url : '',
    );
    const eventMapImageUrl = computed(() => (isFilled.image(props.slice.primary.eventLocationMap) ? props.slice.primary.eventLocationMap.url : ''));

    const hasCalendar = computed((): boolean => !!calendarInviteFileUrl.value && !!props.slice.primary.calendarInviteButtonText);
    const hasLocation = computed((): boolean => !!props.slice.primary.eventLocationTitle && !!eventMapImageUrl.value);
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-agenda {
        container-type: inline-size;

        &__body {
            margin-bottom: var(--joy-core-spacing-8);
        }

        &__title {
            text-align: left;
            margin-bottom: var(--joy-core-spacing-8);
            font-size: var(--joy-font-size-secondary-200);
        }

        &__program,
        &__infos__calendar {
            border-radius: var(--joy-core-radius-4);
            box-shadow: var(--joy-core-elevation-3);
            border: 1px solid var(--joy-color-neutral-30);
            padding: var(--joy-core-spacing-4);
        }

        &__items {
            display: grid;
            gap: var(--joy-core-spacing-6);

            @include mq.screen_xs(min) {
                grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
            }
        }

        &__item {
            padding-bottom: var(--joy-core-spacing-2);
            text-align: left;
            border-bottom: 1px solid var(--joy-color-primary-50);
            font-size: var(--joy-font-size-primary-300);

            &__time {
                font-size: var(--joy-font-size-primary-600);
                font-family: var(--joy-font-family-title);
                font-weight: var(--joy-font-weight-bold);
                color: var(--joy-color-primary-50);
                margin-bottom: var(--joy-core-spacing-1);
            }
        }

        &__infos {
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: var(--joy-core-spacing-5);
            margin-top: var(--joy-core-spacing-5);

            &__calendar {
                display: flex;
                flex-direction: column;
                gap: var(--joy-core-spacing-3);

                &__text {
                    font-size: var(--joy-font-size-secondary-200);
                    text-align: left;
                    width: 100%;
                }

                &__cta {
                    display: flex;
                    align-items: flex-end;
                    gap: var(--joy-core-spacing-3);
                }

                &__button {
                    margin: 0 auto;
                }
            }
        }

        @container (width > 700px) {
            &__body {
                display: flex;
                flex-direction: row;
                gap: var(--joy-core-spacing-6);
                justify-content: center;
                align-items: flex-start;

                // padding: 0 var(--joy-core-spacing-6);
            }

            &__infos {
                margin-top: 0;
            }

            &__program {
                flex: 1;
            }
        }

        @include mq.screen_md(min) {
            padding: 0;
        }

        &--no-calendar,
        &--no-location {
            .slice-agenda__infos {
                &__calendar,
                &__location {
                    width: 100%;
                }
            }
        }
    }
</style>
